import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bot, BookOpen, Cpu, Eye, Boxes, Orbit, Wifi, Glasses, MonitorCog, Workflow, CheckCircle2 } from "lucide-react";
import axios from "axios";
import API from "@/lib/api";

const ICONS = { Bot, BookOpen, Cpu, Eye, Boxes, Orbit, Wifi, Glasses, MonitorCog, Workflow };

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then(({ data }) =>
        setServices(
          (Array.isArray(data) ? data : []).map((s) => ({
            ...s,
            short: s.short_desc,
            build: Array.isArray(s.build_items) ? s.build_items : [],
            image: s.image_url,
          }))
        )
      )
      .catch(console.error);
  }, []);

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative bg-white pt-6 md:pt-10 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#7B2CBF]/25 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[400px] h-[400px] -bottom-20 -left-20 pulse-glow" />
        <div className="container-ngxp relative z-10 text-center max-w-4xl mx-auto">
          <span className="overline">Capabilities</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
            Advanced Technology Capabilities for <span className="gradient-text-glow">Intelligent Enterprise Transformation</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            NGXP Technologies helps organizations build next-generation systems using Agentic AI, AI-native enterprise platforms, digital twins, computer vision, IoT, embedded intelligence, 3D simulation, XR training, and decision intelligence.
          </p>
          <p className="mt-4 text-base text-gray-500 leading-relaxed max-w-3xl mx-auto">
            From intelligent enterprise platforms to real-time operational visibility, NGXP builds technology systems that help organizations improve performance, reduce manual effort, increase visibility, and make faster, smarter decisions.
          </p>
          <div className="mt-9 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary-gradient" data-testid="services-cta">
              Discuss Your Requirement <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a href="#services-list" className="btn-secondary-light">Explore Capabilities</a>
          </div>
        </div>
      </section>

      {/* SECTION 2: INTRODUCTION */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">What We Do</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              What NGXP Can Build for <span className="gradient-text-glow">Your Organization</span>
            </h2>
          </div>
          <div className="mt-8 max-w-3xl space-y-4 text-gray-600 leading-relaxed reveal">
            <p>Enterprises today are not only looking for software development. They are looking for intelligent systems that can solve real business and operational problems.</p>
            <p>NGXP works with organizations that need advanced technology capabilities such as AI automation, Agentic AI, digital twins, real-time monitoring, enterprise intelligence platforms, simulation systems, IoT-enabled visibility, immersive training, and command dashboards.</p>
            <p>Our capabilities are designed for organizations that want to modernize operations, reduce inefficiency, improve decision-making, and build future-ready technology systems with long-term business value.</p>
          </div>
        </div>
      </section>

      {/* SECTION 3: CORE CAPABILITIES */}
      <section id="services-list" className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp space-y-8">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Cpu;
            const reverse = i % 2 === 1;
            return (
              <div key={i} className="ngxp-card !p-0 overflow-hidden reveal" data-testid={`service-${i}`}>
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-0 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                  <div className="lg:col-span-5 relative min-h-[280px] [direction:ltr]">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B10]/60 via-transparent to-transparent" />
                    <div className="relative z-10 p-8">
                      <div className="icon-badge-dark"><Icon className="w-7 h-7" /></div>
                    </div>
                  </div>
                  <div className="lg:col-span-7 p-8 md:p-10 [direction:ltr]">
                    <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0A0B10]">{s.title}</h3>
                    <p className="mt-3 text-gray-600 leading-relaxed">{s.short}</p>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">What We Can Build</h4>
                        <ul className="space-y-2">
                          {s.build.map((b, j) => (
                            <li key={j} className="flex gap-2 items-start text-sm text-gray-700">
                              <CheckCircle2 className="w-4 h-4 text-[#7B2CBF] mt-0.5 shrink-0" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-3">Business Value</h4>
                        <p className="text-sm text-[#0A0B10] font-medium leading-relaxed">{s.value}</p>
                        <Link to="/contact" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold gradient-text-glow" data-testid={`service-${i}-talk`}>
                          Talk to an Engineer <ArrowUpRight className="w-4 h-4 text-[#7B2CBF]" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SECTION 4: HOW WE SELECT THE RIGHT CAPABILITY */}
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="reveal">
              <span className="overline">Our Approach</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                The Right Solution Depends on the <span className="gradient-text-glow">Problem, Not the Technology Trend</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">NGXP does not recommend technology for the sake of technology. We first understand the business problem, operational challenge, users, data flow, constraints, and expected outcome.</p>
              <p className="mt-4 text-gray-600 leading-relaxed">Based on that, we identify whether the right solution requires AI, enterprise platforms, digital twins, IoT, simulation, computer vision, workflow automation, dashboards, or a combination of capabilities.</p>
            </div>
            <div className="reveal">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-6">Our Approach</h3>
              <ol className="space-y-4">
                {[
                  "Understand the real business challenge",
                  "Identify the operational gaps",
                  "Map the data, users, workflows, and decisions",
                  "Choose the right technology architecture",
                  "Build a practical and scalable solution",
                  "Validate business value through real use cases",
                  "Improve and expand the system over time",
                ].map((step, i) => (
                  <li key={i} className="approach-step flex items-start gap-4" style={{ animationDelay: `${i * 0.08}s` }}>
                    <span className="approach-badge shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="approach-text text-gray-700 leading-relaxed pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: BUSINESS OUTCOMES */}
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-30" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-2xl reveal">
            <span className="overline">Impact</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Capabilities Designed for <span className="gradient-text-glow">Measurable Business Impact</span>
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">NGXP's capabilities are designed to help organizations convert complex problems into practical, intelligent, and scalable systems.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 reveal">
            {[
              "Better operational visibility",
              "Faster decision-making",
              "Reduced manual effort",
              "Improved workflow efficiency",
              "Stronger safety and risk awareness",
              "Better reporting and management control",
              "Improved productivity and accountability",
              "Smarter use of enterprise data",
              "Improved workforce training and readiness",
              "Long-term scalability and technology value",
            ].map((outcome, i) => (
              <div key={i} className="impact-card flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4" style={{ animationDelay: `${i * 0.07}s` }}>
                <CheckCircle2 className="impact-icon w-5 h-5 text-[#00A8E8] shrink-0 mt-0.5" />
                <span className="impact-text text-sm text-white/85 leading-relaxed">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: WHEN TO CHOOSE NGXP */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">Ideal Projects</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
                When to Choose <span className="gradient-text-glow">NGXP</span>
              </h2>
              <p className="mt-6 text-gray-600 leading-relaxed">NGXP is the right technology partner when your organization needs more than a standard software vendor.</p>
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">You should consider NGXP when you want to:</p>
              <ul className="space-y-3">
                {[
                  "Build an AI-native enterprise platform",
                  "Create an Agentic AI or AI assistant system",
                  "Develop a digital twin or real-time monitoring system",
                  "Connect IoT, sensors, or field data to enterprise systems",
                  "Use computer vision for operational visibility",
                  "Build immersive 3D or XR training experiences",
                  "Automate complex workflows",
                  "Create command dashboards for leadership visibility",
                  "Modernize ERP, CRM, or operational systems with intelligence",
                  "Build a scalable technology system for future growth",
                ].map((item, i) => (
                  <li key={i} className="choose-item flex items-start gap-3" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="choose-icon w-5 h-5 text-[#7B2CBF] shrink-0 mt-0.5" />
                    <span className="choose-text text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: FINAL CTA */}
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="relative rounded-3xl bg-[#0A0B10] text-white p-10 md:p-16 overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-40" />
            <div className="glow-blob bg-[#FF007F]/30 w-[400px] h-[400px] -top-20 -right-20 pulse-glow" />
            <div className="relative z-10">
              <div className="max-w-3xl">
                <span className="overline">Let's Build</span>
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Let's Build the Right Intelligence Capability for Your Organization</h3>
                <p className="mt-4 text-white/75 leading-relaxed max-w-2xl">
                  Whether your organization needs Agentic AI, AI-native enterprise systems, digital twins, computer vision, IoT intelligence, simulation, XR training, command dashboards, or workflow automation, NGXP can help you design and build a practical, scalable, and future-ready solution.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="services-final-cta" className="btn-primary-gradient">
                  Discuss Your Project <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" data-testid="services-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  Contact NGXP Technologies <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="mt-8 text-white/40 text-xs text-center">NGXP Technologies — Advanced capabilities for intelligent enterprise transformation</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
